import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import cx from "classnames";
import { Divider, Text } from "@flixbus/honeycomb-react";
import type {
  AddCostFormValues,
  CostPanelProps,
  FormulaVariable,
  PlanningMonth,
} from "../../CostPanel.types";
import type { OpDataVariableOption } from "../../../../hooks/useOpDataCatalog";
import EmptyInputsState from "../EmptyInputsState/EmptyInputsState.component";
import CopyAcrossButton from "./CopyAcrossButton.component";
import NumberField from "./NumberField.component";
import ReferencedOpDataSection from "./ReferencedOpDataSection.component";
import { buildAddCostSchema } from "./addCostSchema";
import useGridPasteHandler from "./useGridPasteHandler";
import useCopyAcrossMonths from "./useCopyAcrossMonths";
import styles from "./InputsBody.module.scss";

export const formId = "add-cost-panel-form";

export interface InputsBodyProps {
  formulaExpression: string;
  formulaVariables: FormulaVariable[];
  referencedOpDataVariables: OpDataVariableOption[];
  line: string | null;
  perMonthVars: FormulaVariable[];
  singleVars: FormulaVariable[];
  planningMonths: PlanningMonth[];
  defaultFormValues: AddCostFormValues;
  onSubmit: CostPanelProps["onSubmit"];
  onToggleFormulaMode: () => void;
  noDimensionValues?: boolean;
  dimensionTypeName?: string;
  onEnterEditDimensionValues?: () => void;
}

const InputsBody: React.FC<InputsBodyProps> = ({
  formulaExpression,
  formulaVariables,
  referencedOpDataVariables,
  line,
  perMonthVars,
  singleVars,
  planningMonths,
  defaultFormValues,
  onSubmit,
  onToggleFormulaMode,
  noDimensionValues = false,
  dimensionTypeName,
  onEnterEditDimensionValues,
}) => {
  const { control, handleSubmit, setValue } = useForm<AddCostFormValues>({
    values: defaultFormValues,
    resolver: yupResolver(buildAddCostSchema(formulaVariables, planningMonths)),
    mode: "onTouched",
  });

  const getGridPasteHandler = useGridPasteHandler(
    perMonthVars,
    planningMonths,
    setValue,
  );
  const copyAcrossMonths = useCopyAcrossMonths(planningMonths, setValue);

  const opDataSection = referencedOpDataVariables.length > 0 && (
    <>
      <Divider />
      <ReferencedOpDataSection
        referencedOpDataVariables={referencedOpDataVariables}
        planningMonths={planningMonths}
        line={line}
      />
    </>
  );

  if (formulaVariables.length === 0) {
    const reason =
      formulaExpression.trim() === "" ? "noFormula" : "noFormulaVariables";
    return (
      <div className={styles.bodyContainer}>
        <EmptyInputsState
          reason={reason}
          onEnterFormulaMode={onToggleFormulaMode}
          noDimensionValues={noDimensionValues}
          dimensionTypeName={dimensionTypeName}
          onSelectValues={onEnterEditDimensionValues}
        />
        {opDataSection}
      </div>
    );
  }
  return (
    <div className={styles.bodyContainer}>
      <form
        id={formId}
        aria-label="Add cost"
        onSubmit={handleSubmit((data) => onSubmit(data))}
        className={styles.formContainer}
      >
        {perMonthVars.length > 0 && (
          <div className={styles.perMonthGroup}>
            <div className={styles.perMonthSection}>
              {perMonthVars.map((variable, rowIndex) => (
                <div key={variable.id} className={styles.perMonthRow}>
                  <Text Elem="span" extraClasses={styles.variableLabel}>
                    {variable.unit
                      ? `${variable.name} (${variable.unit})`
                      : variable.name}
                  </Text>
                  <div className={styles.monthInputs}>
                    {planningMonths.map((month, colIndex) => {
                      const isJanuaryWithLaterMonths =
                        colIndex === 0 && planningMonths.length > 1;
                      const field = (
                        <NumberField
                          name={`${variable.id}.${month.key}`}
                          id={`${variable.id}_${month.key}`}
                          label={rowIndex === 0 ? month.label : ""}
                          ariaLabel={`${variable.name} ${month.label}`}
                          extraClasses={cx(styles.monthInput, {
                            [styles.januaryInput]: isJanuaryWithLaterMonths,
                          })}
                          control={control}
                          onGridPaste={getGridPasteHandler(rowIndex, colIndex)}
                        />
                      );
                      if (!isJanuaryWithLaterMonths) {
                        return (
                          <React.Fragment key={month.key}>
                            {field}
                          </React.Fragment>
                        );
                      }
                      return (
                        <div key={month.key} className={styles.januaryCell}>
                          {field}
                          <CopyAcrossButton
                            variableId={variable.id}
                            variableName={variable.name}
                            januaryFieldName={`${variable.id}.${month.key}`}
                            control={control}
                            onCopy={copyAcrossMonths}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Text small extraClasses={styles.pasteTip}>
              {"Tip: You can copy paste cells from Excel into this table."}
            </Text>
          </div>
        )}

        {perMonthVars.length > 0 && singleVars.length > 0 && <Divider />}

        {singleVars.length > 0 && (
          <div className={styles.singleInputGrid}>
            {singleVars.map((variable) => (
              <NumberField
                key={variable.id}
                name={variable.id}
                id={variable.id}
                label={variable.name}
                unit={variable.unit}
                control={control}
              />
            ))}
          </div>
        )}
      </form>
      {opDataSection}
    </div>
  );
};

export default InputsBody;
